import React, { useState, useEffect, useMemo, useRef } from 'react';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Column, ColumnBodyOptions, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';

import propertyImportsService from '../../service/api/PropertyImportsService';
import { Tag } from 'primereact/tag';
import PropertyImportDialog from './PropertyImportDialog';
import { Trans, useTranslation } from 'react-i18next';
import { PropertyImport } from '../../types/PropertyImport';

import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { getFileFromBuffer } from '../_shared/getFile';
import { Dropdown } from 'primereact/dropdown';
import enumService from '../../service/EnumService';
import { Toast } from 'primereact/toast';
import { FULL_DATE_FORMAT, TOAST_LIFETIME } from '../../settings';
import { Dialog } from 'primereact/dialog';

export const PropertyImportManagement = () => {
  const toast = useRef<any>();
  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [propertyImports, setPropertyImports] = useState<PropertyImport[]>(null);

  const [selectedRowToEdit, setSelectedRowToEdit] = useState<PropertyImport>(null);
  const [addEditDialogVisible, setAddEditDialogVisible] = useState(false);
  const [printInProgress, setPrintInProgress] = useState(false);
  const { t } = useTranslation();
  const [importToDelete, setImportToDelete] = useState<PropertyImport>();
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);

  const [filters, setFilters] = useState<DataTableFilterMeta>({});

  const initFilters = () => {
    setFilters({
      name: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },

      creation: { value: null, matchMode: FilterMatchMode.EQUALS },
    });
    setGlobalFilter('');
  };

  const showToast = (summary: string, detail: string, severity = 'success') => {
    toast.current.show({
      severity: severity,
      summary: summary,
      detail: detail,
      life: TOAST_LIFETIME,
    });
  };

  const loadData = async () => {
    setLoading(true);
    const propertyImportsResponse = await propertyImportsService.getAll();

    propertyImportsResponse.forEach((_x) => {});
    setPropertyImports(propertyImportsResponse);
    setLoading(false);
    initFilters();
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const propertyImportsTableHeader = (
    <div className="table-header">
      {t('propertyImports.header')}
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder={t('common.search')}
        />
        <Button
          type="button"
          icon="pi pi-plus"
          label={t('common.add')}
          className="p-button ml-2"
          onClick={() => {
            addPropertyImport();
          }}
        ></Button>
      </span>
    </div>
  );

  const confirmDelete = (propertyImport: PropertyImport) => {
    setImportToDelete(propertyImport);
    setDeleteDialogVisible(true);
  };

  const deleteImport = () => {
    setLoading(true);
    propertyImportsService.delete(importToDelete.id).then(() => {
      loadData();
    });

    setDeleteDialogVisible(false);
  };
  const hideDeleteDialog = () => {
    setDeleteDialogVisible(false);
  };

  const deleteDialogFooter = (
    <>
      <Button label={t('common.no')} icon="pi pi-times" className="p-button-text" onClick={() => hideDeleteDialog()} />
      <Button label={t('common.yes')} icon="pi pi-check" className="p-button-text" onClick={() => deleteImport()} />
    </>
  );

  const closeAddEditModal = () => {
    setAddEditDialogVisible(false);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadData();
  };

  const addPropertyImport = () => {
    setSelectedRowToEdit(null);
    setAddEditDialogVisible(true);
  };

  const print = async (_propertyImport: PropertyImport) => {
    setPrintInProgress(true);
    try {
      const response = await propertyImportsService.print(_propertyImport.id);
      // file name intentionally left blank to open pdf in new tab
      getFileFromBuffer(response, 'application/pdf', null);
    } finally {
      setPrintInProgress(false);
    }
  };

  const bodyTemplate = (data: PropertyImport, props: ColumnBodyOptions) => {
    return <>{data[props.field]}</>;
  };

  const calendarColumnTemplate = (data: PropertyImport, props: ColumnBodyOptions) => {
    const value = data[props.field];
    const formattedValue = value ? format(value, FULL_DATE_FORMAT) : '';
    return <>{formattedValue}</>;
  };

  const boolColumnTemplate = (
    data: PropertyImport,
    props: ColumnBodyOptions,
    trueValue: string,
    falseValue: string
  ) => {
    let result: any = '';

    if (data[props.field] === true) {
      result = <Tag className="mr-2" icon="pi pi-check" severity="success" value={trueValue}></Tag>;
    } else if (data[props.field] === false) {
      result = <Tag className="mr-2" icon="pi pi-times-circle" severity="danger" value={falseValue}></Tag>;
    }
    return result;
  };

  const importTypes = useMemo(() => enumService.getPropertyImportTypes(t), [t]);
  const typeFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return (
      <Dropdown
        value={options.value}
        onChange={(e) => options.filterCallback(e.value)}
        options={importTypes}
        placeholder={t('common.select')}
      />
    );
  };

  const actionTemplate = (data: PropertyImport) => (
    <span>
      <Button
        type="button"
        icon="pi pi-print"
        tooltip={t('common.print')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success mr-2"
        disabled={printInProgress}
        onClick={async () => {
          await print(data);
        }}
      ></Button>
      <Button
        type="button"
        icon="pi pi-trash"
        tooltip={t('common.delete')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success mr-2"
        onClick={() => {
          confirmDelete(data);
        }}
      ></Button>
    </span>
  );

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={propertyImports}
            paginator
            className="p-datatable-customers"
            rows={10}
            dataKey="id"
            rowHover
            globalFilter={globalFilter}
            emptyMessage={t('propertyImports.empty_message')}
            loading={loading}
            header={propertyImportsTableHeader}
            filterDisplay="menu"
            filters={filters}
            onFilter={(e) => {
              setFilters(e.filters);
            }}
          >
            <Column
              field="name"
              header={t('propertyImports.name')}
              sortable
              body={bodyTemplate}
              filter
              showFilterMatchModes={false}
              showFilterMenuOptions={false}
            ></Column>
            <Column
              field="creationDate"
              header={t('propertyImports.creation_date')}
              sortable
              body={calendarColumnTemplate}
            ></Column>
            <Column
              field="creation"
              header={t('propertyImports.type')}
              sortable
              body={(data, props) =>
                boolColumnTemplate(data, props, t('propertyImports.creation'), t('propertyImports.deletion'))
              }
              filter
              filterElement={typeFilterTemplate}
              dataType="boolean"
            ></Column>
            <Column
              headerStyle={{ width: '8rem' }}
              header={t('common.actions')}
              align="center"
              body={actionTemplate}
            ></Column>
          </DataTable>
          <PropertyImportDialog
            dialogVisible={addEditDialogVisible}
            closeAddEditModal={closeAddEditModal}
            readOnly={false}
            propertyImport={selectedRowToEdit}
            showToast={showToast}
          />
          <Dialog
            visible={deleteDialogVisible}
            className="p-fluid"
            header={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                {t('common.confirm')}
              </span>
            }
            modal
            footer={deleteDialogFooter}
            onHide={hideDeleteDialog}
            breakpoints={{ '896px': '90vw' }}
            style={{ minWidth: '450px' }}
          >
            <div className="confirmation-content">
              {importToDelete && (
                <span>
                  <Trans
                    i18nKey="common.confirm_delete_message"
                    values={{ name: importToDelete.name }}
                    components={[<strong />]}
                  />
                </span>
              )}
            </div>
          </Dialog>
        </div>
      </div>
      <Toast ref={toast} />
    </div>
  );
};
